import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, generateId } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format, differenceInDays } from 'date-fns';
import { Plus, Pencil, Trash2, CalendarIcon, Target, TrendingUp, RefreshCw } from 'lucide-react';
import type { SavingsGoal, ContributionFrequency } from '@/lib/types';

const FREQUENCY_LABELS: Record<ContributionFrequency, string> = {
  none: 'None',
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
};

export default function SavingsGoalsPage() {
  const { data, updateData } = useAuth();
  const goals = data.savingsGoals ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SavingsGoal | null>(null);
  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [currentAmount, setCurrentAmount] = useState('');
  const [targetDate, setTargetDate] = useState<Date | undefined>();
  const [contribAmount, setContribAmount] = useState('');
  const [contribFreq, setContribFreq] = useState<ContributionFrequency>('none');

  const sortedGoals = useMemo(() => {
    return [...goals].sort((a, b) => {
      const aPct = a.targetAmount > 0 ? a.currentAmount / a.targetAmount : 0;
      const bPct = b.targetAmount > 0 ? b.currentAmount / b.targetAmount : 0;
      // Incomplete first, sorted by closest to target date
      if (aPct >= 1 && bPct < 1) return 1;
      if (bPct >= 1 && aPct < 1) return -1;
      return new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime();
    });
  }, [goals]);

  const totalTarget = goals.reduce((s, g) => s + g.targetAmount, 0);
  const totalSaved = goals.reduce((s, g) => s + Math.min(g.currentAmount, g.targetAmount), 0);

  const openAdd = () => {
    setEditing(null);
    setName('');
    setTargetAmount('');
    setCurrentAmount('0');
    setTargetDate(undefined);
    setContribAmount('');
    setContribFreq('none');
    setDialogOpen(true);
  };

  const openEdit = (g: SavingsGoal) => {
    setEditing(g);
    setName(g.name);
    setTargetAmount(String(g.targetAmount));
    setCurrentAmount(String(g.currentAmount));
    setTargetDate(new Date(g.targetDate));
    setContribAmount(g.contributionAmount ? String(g.contributionAmount) : '');
    setContribFreq(g.contributionFrequency ?? 'none');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const target = parseFloat(targetAmount);
    const current = parseFloat(currentAmount);
    if (!name.trim() || isNaN(target) || target <= 0 || isNaN(current) || current < 0 || !targetDate) return;

    const contrib = parseFloat(contribAmount);
    const contribVal = !isNaN(contrib) && contrib > 0 ? contrib : undefined;

    if (editing) {
      await updateData(prev => ({
        ...prev,
        savingsGoals: (prev.savingsGoals ?? []).map(g =>
          g.id === editing.id
            ? { ...g, name: name.trim(), targetAmount: target, currentAmount: current, targetDate: targetDate.toISOString().split('T')[0], contributionAmount: contribVal, contributionFrequency: contribFreq }
            : g
        ),
      }));
    } else {
      const goal: SavingsGoal = {
        id: generateId(),
        name: name.trim(),
        targetAmount: target,
        currentAmount: current,
        targetDate: targetDate.toISOString().split('T')[0],
        contributionAmount: contribVal,
        contributionFrequency: contribFreq,
        createdAt: new Date().toISOString(),
      };
      await updateData(prev => ({
        ...prev,
        savingsGoals: [...(prev.savingsGoals ?? []), goal],
      }));
    }
    setDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    await updateData(prev => ({
      ...prev,
      savingsGoals: (prev.savingsGoals ?? []).filter(g => g.id !== id),
    }));
  };

  const handleAddFunds = async (id: string, amount: number) => {
    await updateData(prev => ({
      ...prev,
      savingsGoals: (prev.savingsGoals ?? []).map(g =>
        g.id === id ? { ...g, currentAmount: g.currentAmount + amount } : g
      ),
    }));
  };

  const [fundDialogGoalId, setFundDialogGoalId] = useState<string | null>(null);
  const [fundAmount, setFundAmount] = useState('');

  return (
    <div className="px-4 pt-6 pb-24">
      <h1 className="mb-4 font-display text-2xl font-bold">Savings Goals</h1>

      {/* Overall progress */}
      {goals.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Overall savings</span>
              <span className="font-semibold">{formatAUD(totalSaved)} / {formatAUD(totalTarget)}</span>
            </div>
            <Progress
              value={totalTarget > 0 ? Math.min((totalSaved / totalTarget) * 100, 100) : 0}
              className="mt-2 h-2"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {goals.length} goal{goals.length !== 1 ? 's' : ''} · {goals.filter(g => g.currentAmount >= g.targetAmount).length} completed
            </p>
          </CardContent>
        </Card>
      )}

      <Button onClick={openAdd} className="mb-4 w-full">
        <Plus className="mr-1 h-4 w-4" /> Add Savings Goal
      </Button>

      {sortedGoals.length === 0 ? (
        <div className="mt-8 text-center">
          <Target className="mx-auto h-12 w-12 text-muted-foreground/40" />
          <p className="mt-3 text-muted-foreground">No savings goals yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Set a target and start saving!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedGoals.map(g => {
            const pct = g.targetAmount > 0 ? (g.currentAmount / g.targetAmount) * 100 : 0;
            const completed = pct >= 100;
            const daysLeft = differenceInDays(new Date(g.targetDate), new Date());
            const overdue = daysLeft < 0 && !completed;
            const remaining = g.targetAmount - g.currentAmount;
            const dailyNeeded = daysLeft > 0 && remaining > 0 ? remaining / daysLeft : 0;

            return (
              <Card key={g.id} className={cn(completed && 'border-success/40 bg-success/5')}>
                <CardContent className="p-4">
                  <div className="mb-2 flex items-start justify-between">
                    <div>
                      <p className="font-medium">{g.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Target: {format(new Date(g.targetDate), 'dd MMM yyyy')}
                        {completed ? (
                          <span className="ml-1 text-success">✓ Reached!</span>
                        ) : overdue ? (
                          <span className="ml-1 text-destructive">· Overdue</span>
                        ) : (
                          <span> · {daysLeft} day{daysLeft !== 1 ? 's' : ''} left</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setFundDialogGoalId(g.id); setFundAmount(''); }}>
                        <TrendingUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(g)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete goal?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Remove "{g.name}" savings goal?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(g.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-semibold">{formatAUD(g.currentAmount)}</span>
                    <span className="text-muted-foreground">{formatAUD(g.targetAmount)}</span>
                  </div>
                  <Progress
                    value={Math.min(pct, 100)}
                    className={cn(
                      'h-2.5',
                      completed ? '[&>div]:bg-success' : overdue ? '[&>div]:bg-destructive' : '[&>div]:bg-primary'
                    )}
                  />
                  <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{Math.round(pct)}% saved</span>
                    {!completed && dailyNeeded > 0 && (
                      <span>{formatAUD(dailyNeeded)}/day to reach goal</span>
                    )}
                  </div>
                  {g.contributionFrequency && g.contributionFrequency !== 'none' && g.contributionAmount && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-primary">
                      <RefreshCw className="h-3 w-3" />
                      <span>Auto: {formatAUD(g.contributionAmount)} {FREQUENCY_LABELS[g.contributionFrequency].toLowerCase()}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Goal' : 'New Savings Goal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Goal Name</label>
              <Input placeholder="e.g. Emergency Fund" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Target Amount ($)</label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={targetAmount} onChange={e => setTargetAmount(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Already Saved ($)</label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={currentAmount} onChange={e => setCurrentAmount(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Target Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-full justify-start text-left font-normal', !targetDate && 'text-muted-foreground')}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {targetDate ? format(targetDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={targetDate}
                    onSelect={setTargetDate}
                    initialFocus
                    className={cn('p-3 pointer-events-auto')}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Auto-Contribute Frequency</label>
              <Select value={contribFreq} onValueChange={v => setContribFreq(v as ContributionFrequency)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(FREQUENCY_LABELS) as [ContributionFrequency, string][]).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {contribFreq !== 'none' && (
              <div>
                <label className="mb-1 block text-sm font-medium">Contribution Amount ($)</label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={contribAmount} onChange={e => setContribAmount(e.target.value)} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!name.trim() || !targetAmount || parseFloat(targetAmount) <= 0 || !targetDate}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Funds Dialog */}
      <Dialog open={!!fundDialogGoalId} onOpenChange={open => { if (!open) setFundDialogGoalId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Funds</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="mb-1 block text-sm font-medium">Amount ($)</label>
            <Input type="number" min="0" step="0.01" placeholder="0.00" value={fundAmount} onChange={e => setFundAmount(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFundDialogGoalId(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const amt = parseFloat(fundAmount);
                if (fundDialogGoalId && !isNaN(amt) && amt > 0) {
                  handleAddFunds(fundDialogGoalId, amt);
                  setFundDialogGoalId(null);
                }
              }}
              disabled={!fundAmount || parseFloat(fundAmount) <= 0}
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
